import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { OrgUnitTreeSelect } from '@/components/forms/OrgUnitTreeSelect';
import { collectSubtreeIds } from '@/lib/hierarchyTree';
import { OrgUnit } from '@/types/core/OrgUnit';

interface MoveOrgUnitDialogProps {
  open: boolean;
  /** The node being moved. */
  orgUnit: OrgUnit;
  /** All org units — the source for both the picker and the descendant exclusion. */
  orgUnits: OrgUnit[];
  /** Open with "move to root" pre-selected (the context menu's dedicated entry). */
  defaultToRoot?: boolean;
  onClose: () => void;
  /** `undefined` means "make this a root node". */
  onMove: (orgUnit: OrgUnit, newParentId: string | undefined) => void;
  isMoving: boolean;
}

export function MoveOrgUnitDialog({
  open,
  orgUnit,
  orgUnits,
  defaultToRoot = false,
  onClose,
  onMove,
  isMoving,
}: MoveOrgUnitDialogProps) {
  const { t } = useTranslation();
  const [moveToRoot, setMoveToRoot] = useState(defaultToRoot);
  const [parentId, setParentId] = useState<string | null>(null);

  // Reset whenever the dialog opens for a (possibly different) node.
  useEffect(() => {
    if (open) {
      setMoveToRoot(defaultToRoot);
      setParentId(null);
    }
  }, [open, orgUnit.id, defaultToRoot]);

  const excludeIds = useMemo(
    () => collectSubtreeIds(orgUnits, orgUnit.id),
    [orgUnits, orgUnit.id],
  );

  const nextParentId = moveToRoot ? undefined : (parentId ?? undefined);
  const unchanged = nextParentId === (orgUnit.parentId ?? undefined);
  const nothingPicked = !moveToRoot && !parentId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('orgUnit.moveDialogTitle', { name: orgUnit.name })}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Typography variant="body2" color="text.secondary">
          {t('orgUnit.moveHint')}
        </Typography>
        <Box>
          <OrgUnitTreeSelect
            value={parentId}
            onChange={setParentId}
            label={t('orgUnit.newParent')}
            excludeIds={excludeIds}
            disabled={moveToRoot}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={moveToRoot}
              onChange={(e) => setMoveToRoot(e.target.checked)}
            />
          }
          label={t('orgUnit.moveToRoot')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => onMove(orgUnit, nextParentId)}
          disabled={isMoving || nothingPicked || unchanged}
        >
          {isMoving ? t('orgUnit.moving') : t('orgUnit.move')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
